import {
  MutableRefObject,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface UserInfo {
  tenantId: string;
  tenantName?: string;
  userId: string;
}

type GetUserInfoCallback = () => UserInfo | undefined;

interface UserContextValue {
  userInfo?: UserInfo;
  getUserInfoRef: MutableRefObject<GetUserInfoCallback>;
  setUserInfo: (userInfo?: UserInfo) => void;
}

interface UserProviderProps {
  userInfo?: UserInfo;
  children: ReactNode;
}

const UserContext = createContext<UserContextValue | null>(null);

const UserProvider = (props: UserProviderProps) => {
  const { children } = props;

  const [userInfo, setUserInfo] = useState<UserInfo | undefined>();

  const getUserInfoRef = useRef<GetUserInfoCallback | undefined>(undefined);

  useEffect(() => {
    if (props.userInfo) {
      setUserInfo(props.userInfo);
    }
  }, [props.userInfo]);

  const value = useMemo<UserContextValue | undefined>(() => {
    // keep get user info ref up to date
    getUserInfoRef.current = () => {
      return userInfo;
    };

    return {
      getUserInfoRef: getUserInfoRef as MutableRefObject<GetUserInfoCallback>,
      setUserInfo,
      userInfo,
    };
  }, [userInfo]);

  return value === undefined ? null : (
    <UserContext.Provider value={value}>{children}</UserContext.Provider>
  );
};

const useUserProvider = (): UserContextValue => {
  return useContext(UserContext) as UserContextValue;
};

export { UserProvider, useUserProvider };
